import React, { useState, useEffect, useCallback } from 'react';
import {
  Form, Input, Select, DatePicker, InputNumber,
  Button, Row, Col, Modal, message, TimePicker, Tooltip
} from 'antd';
import moment from 'moment';
import ConfirmModal from '../ConfirmModal';
import { useSelector, useDispatch } from 'react-redux';
import { fetchUsersByOrganisation, clearOrganisationUsers } from '../../redux/userOrganisationsSlice';
import { canEditVacancy, canCreateVacancyFromRequest } from '../../utils/vacancyPermissions';


const { Option } = Select;
const { TextArea } = Input;

const workSchedules = [
  '6/1', '5/2', '4/4', '4/3', '4/2', '3/3', '2/2', '1/3', '1/2',
  'По выходным', 'Свободный', 'Другое'
];

const vacancyReasons = [
  'Новая вакансия',
  'Внутренний перевод',
  'Декрет',
  'Замена'
];

const EditModal = ({ isOpen, onClose, vacancy, onSubmit, organisations, users }) => {
  const dispatch = useDispatch();
  const { role } = useSelector((state) => state.auth);
  const { organisationUsers } = useSelector((state) => state.userOrganisations);
  const [form] = Form.useForm();
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isHiddenVacancy, setIsHiddenVacancy] = useState(false);
  const [businessTripsRequired, setBusinessTripsRequired] = useState(false);
  const [relocationWillingness, setRelocationWillingness] = useState(false);
  const [communicationCompensation, setCommunicationCompensation] = useState(false);
  const [hasCar, setHasCar] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [initialValues, setInitialValues] = useState({});

  const carCompensationValue = Form.useWatch('car_compensation', form);
  const driverLicenseRequiredValue = Form.useWatch('driver_license_required', form);
  const selectedOrganisationId = Form.useWatch('organisation_id', form);

  const handleCheckboxToggle = (fieldName) => {
    const currentValue = form.getFieldValue(fieldName) || false;
    form.setFieldsValue({ [fieldName]: !currentValue });
  };

  const filterDepartments = useCallback((orgId) => {
    const organisation = organisations?.find(org => org.organisation_id === orgId);
    if (organisation && organisation.departments) {
      setFilteredDepartments(organisation.departments);
    } else {
      setFilteredDepartments([]);
    }
  }, [organisations]);

  useEffect(() => {
    if (isOpen && vacancy) {
      const parseSalary = (salary) => {
        if (!salary) return { min: null, max: null };

        // Если зарплата содержит диапазон (например, "1000-2000" или "1000 - 2000")
        if (typeof salary === 'string' && salary.includes('-')) {
          const parts = salary.split('-').map(part => part.trim());
          const min = parseFloat(parts[0]) || null;
          const max = parseFloat(parts[1]) || null;
          return { min, max };
        }

        // Если зарплата - одно число
        const numSalary = parseFloat(salary);
        return {
          min: isNaN(numSalary) ? null : numSalary,
          max: isNaN(numSalary) ? null : numSalary
        };
      };

      const probationSalary = parseSalary(vacancy.trial_salary);
      const postProbationSalary = parseSalary(vacancy.post_trial_salary);

      const formattedVacancy = {
        ...vacancy,
        // Даты
        opening_date: vacancy.open_date ? moment(vacancy.open_date) : undefined,
        closing_date: vacancy.close_date ? moment(vacancy.close_date) : undefined,
        planned_start_date: vacancy.planned_start_date ? vacancy.planned_start_date : undefined,
        // Время - обрабатываем как строки или moment объекты
        work_start_time: vacancy.work_start_time ? moment(vacancy.work_start_time, 'HH:mm') : undefined,
        work_end_time: vacancy.work_end_time ? moment(vacancy.work_end_time, 'HH:mm') : undefined,
        // Массивы для Form.List
        software_knowledge: vacancy.software_skills || [],
        language_knowledge: vacancy.language_skills || [],
        // Основные поля
        count: vacancy.positions_count,
        job_address: vacancy.workplace_address,
        work_location_ratio: vacancy.field_vs_office,
        business_trips_details: vacancy.travel_details,
        car_compensation: vacancy.compensate_car || false,
        salary_structure: vacancy.salary_structure,
        net_probation_salary_min: probationSalary.min,
        net_probation_salary_max: probationSalary.max,
        net_post_probation_salary_min: postProbationSalary.min,
        net_post_probation_salary_max: postProbationSalary.max,
        gross_probation_salary_min: probationSalary.min,
        gross_probation_salary_max: probationSalary.max,
        gross_post_probation_salary_min: postProbationSalary.min,
        gross_post_probation_salary_max: postProbationSalary.max,
        driver_license_required: vacancy.requires_license || false,
        driver_license_categories: vacancy.license_categories || [],
        pc_skills: vacancy.computer_skills_level,
        work_experience: vacancy.experience_description,
        personal_qualities: vacancy.personal_traits,
        professional_skills: vacancy.professional_skills,
        tasks: vacancy.tasks,
        expected_results: vacancy.expected_results,
        responsibilities: vacancy.functional_responsibilities,
        reporting: vacancy.reporting_details,
        subordinate_count: vacancy.subordinates_count,
        creator_position: vacancy.author_position,
        gender: vacancy.gender,
        min_age: vacancy.age?.split('-')?.[0],
        max_age: vacancy.age?.split('-')?.[1],
        education: vacancy.education_levels?.[0],
        preferred_companies: vacancy.preferred_companies,
        unwanted_companies: vacancy.unwanted_companies,
        other_conditions: vacancy.other_conditions,
        supervisors: vacancy.supervisors
      };

      // Сохраняем начальные значения для сравнения
      setInitialValues(formattedVacancy);
      form.setFieldsValue(formattedVacancy);

      // Автоматически заполняем поле author именем текущего пользователя
      setTimeout(() => {
        const firstName = localStorage.getItem('first_name') || '';
        const lastName = localStorage.getItem('last_name') || '';
        const authorName = `${firstName} ${lastName}`.trim();

        if (authorName) {
          form.setFieldsValue({
            author: authorName
          });
        }
      }, 0);

      // Устанавливаем состояния кнопок-чекбоксов
      setIsHiddenVacancy(vacancy.is_hidden || false);
      setBusinessTripsRequired(vacancy.requires_travel || false);
      setRelocationWillingness(vacancy.requires_relocation || false);
      setCommunicationCompensation(vacancy.compensate_phone || false);
      setHasCar(vacancy.requires_car || false);

      if (vacancy.organisation_id) {
        filterDepartments(vacancy.organisation_id);
      } else {
        dispatch(clearOrganisationUsers());
      }
    } else {
      dispatch(clearOrganisationUsers());
    }
  }, [isOpen, vacancy, form, filterDepartments, dispatch]);

  const handleCompanyChange = (orgId) => {
    form.setFieldsValue({ department_id: undefined, assigner_id: undefined });
    filterDepartments(orgId);
  };

  useEffect(() => {
    if (selectedOrganisationId) {
      dispatch(fetchUsersByOrganisation(selectedOrganisationId));
    } else {
      dispatch(clearOrganisationUsers());
    }
  }, [selectedOrganisationId, dispatch]);

  const hasChanges = useCallback(() => {
    const currentValues = form.getFieldsValue();
    const changedFields = {};

    Object.keys(currentValues).forEach(key => {
      if (currentValues[key] !== initialValues[key]) {
        changedFields[key] = currentValues[key];
      }
    });

    // Проверяем изменения в состояниях чекбоксов
    const checkboxChanges =
      isHiddenVacancy !== (initialValues.is_hidden || false) ||
      businessTripsRequired !== (initialValues.requires_travel || false) ||
      relocationWillingness !== (initialValues.requires_relocation || false) ||
      communicationCompensation !== (initialValues.compensate_phone || false) ||
      hasCar !== (initialValues.requires_car || false);

    return Object.keys(changedFields).length > 0 || checkboxChanges;
  }, [form, initialValues, isHiddenVacancy, businessTripsRequired, relocationWillingness, communicationCompensation, hasCar]);

  const handleCancel = useCallback(() => {
    if (hasChanges()) {
      setShowConfirm(true);
    } else {
      onClose();
    }
  }, [hasChanges, onClose]);

  const handleConfirmClose = useCallback((shouldClose) => {
    if (shouldClose) {
      onClose();
    }
    setShowConfirm(false);
  }, [onClose]);

  const handleSubmit = async (createFromDraft = false) => {
    const { canEdit, reason } = canEditVacancy(role);
    if (!canEdit) {
      message.warning(reason);
      return;
    }

    try {
      setLoading(true);
      // Для создания вакансии из черновика выполняем валидацию обязательных полей,
      // для обычного сохранения — пропускаем валидацию и берем текущие значения формы
      const values = createFromDraft
        ? await form.validateFields()
        : form.getFieldsValue(true);

      // Определяем измененные поля
      const changedFields = {};
      Object.keys(values).forEach(key => {
        if (values[key] !== initialValues[key]) {
          changedFields[key] = values[key];
        }
      });

      // Добавляем изменения состояний чекбоксов
      if (isHiddenVacancy !== (initialValues.is_hidden || false)) {
        changedFields.is_hidden = isHiddenVacancy;
      }
      if (businessTripsRequired !== (initialValues.requires_travel || false)) {
        changedFields.business_trips_required = businessTripsRequired;
      }
      if (relocationWillingness !== (initialValues.requires_relocation || false)) {
        changedFields.relocation_willingness = relocationWillingness;
      }
      if (communicationCompensation !== (initialValues.compensate_phone || false)) {
        changedFields.communication_compensation = communicationCompensation;
      }
      if (hasCar !== (initialValues.requires_car || false)) {
        changedFields.has_car = hasCar;
      }
      if (carCompensationValue !== (initialValues.compensate_car || false)) {
        changedFields.compensate_car = carCompensationValue;
      }

      // Проверяем, есть ли изменения
      if (Object.keys(changedFields).length === 0) {
        message.info('Нет изменений для сохранения');
        onClose();
        return;
      }

      // Удаляем поле author из отправляемых данных, так как оно только для отображения
      delete changedFields.author;

      // Форматируем даты и время только если они изменились
      if (changedFields.closing_date) {
        changedFields.closing_date = changedFields.closing_date.format('YYYY-MM-DD');
      }

      if (changedFields.work_start_time) {
        changedFields.work_start_time = changedFields.work_start_time.format('HH:mm');
      }

      if (changedFields.work_end_time) {
        changedFields.work_end_time = changedFields.work_end_time.format('HH:mm');
      }

      // Добавляем поля для скрытой вакансии только если они изменились
      if (changedFields.hasOwnProperty('is_hidden')) {
        if (changedFields.is_hidden) {
          changedFields.is_hidden = true;
          changedFields.visible_user_ids = changedFields.visible_user_ids || [];
        } else {
          changedFields.is_hidden = false;
          changedFields.visible_user_ids = [];
        }
      }

      // Обрабатываем массивы языков и программ - переименовываем поля для API только если они изменились
      if (changedFields.language_knowledge && Array.isArray(changedFields.language_knowledge)) {
        changedFields.language_skills = changedFields.language_knowledge;
        delete changedFields.language_knowledge;
      }

      if (changedFields.software_knowledge && Array.isArray(changedFields.software_knowledge)) {
        changedFields.software_skills = changedFields.software_knowledge;
        delete changedFields.software_knowledge;
      }

      // Обрабатываем поля водительского удостоверения - переименовываем поля для API только если они изменились
      if (changedFields.hasOwnProperty('driver_license_required')) {
        changedFields.requires_license = changedFields.driver_license_required;
        delete changedFields.driver_license_required;
      }

      if (changedFields.driver_license_categories && Array.isArray(changedFields.driver_license_categories)) {
        changedFields.license_categories = changedFields.driver_license_categories;
        delete changedFields.driver_license_categories;
      }

      // Если создаем вакансию из черновика, добавляем соответствующие поля
      if (createFromDraft) {
        changedFields.is_template = false;
        changedFields.status = 'new';
      }

      await onSubmit(vacancy.id, values.organisation_id, changedFields);
      onClose();
    } catch (error) {
      if (error?.errorFields?.length > 0) {
        form.scrollToField(error.errorFields[0].name, {
          behavior: 'smooth',
          block: 'center'
        });
        message.error('Пожалуйста, заполните обязательные поля');
      }
    } finally {
      setLoading(false);
    }
  };

  const isDraft = (vac) => vac?.is_template === true || vac?.status === 'draft';
  const { canEdit: hasEditPermission } = canEditVacancy(role);

  const footerButtons = [
    <Tooltip key="back" title="Закрыть модальное окно без сохранения изменений">
      <Button key="cancel" onClick={handleCancel}>
        Закрыть
      </Button>
    </Tooltip>,
    <Tooltip key="submit" title={hasEditPermission ? "Сохранить изменения в вакансии" : "У вас нет прав для сохранения изменений в вакансии"}>
      <Button
        key="submit"
        type="primary"
        loading={loading}
        onClick={() => handleSubmit(false)}
        disabled={!hasEditPermission}
      >
        Сохранить
      </Button>
    </Tooltip>
  ];

  if (isDraft(vacancy)) {
    footerButtons.push(
      <Tooltip title={canCreateVacancyFromRequest(role) ? "Создать новую вакансию" : "У вас нет прав для создания вакансии"}>
        <Button
          key="create"
          type="primary"
          loading={loading}
          onClick={() => handleSubmit(true)}
          style={{ marginLeft: 8 }}
          disabled={!hasEditPermission}
        >
          Создать вакансию
        </Button>
      </Tooltip >
    );
  }

  return (
    <>
      <Modal
        title="Редактировать вакансию"
        visible={undefined}
        open={isOpen}
        onCancel={handleCancel}
        width={1000}
        footer={footerButtons}
      >
        <Form form={form} layout="vertical" scrollToFirstError={{ behavior: 'smooth', block: 'center' }}>
          {/* Основная информация о вакансии */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Основная информация
            </h3>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="job_title" label="Название должности" rules={[{ required: true, message: 'Пожалуйста, введите название должности' }]}>
                  <Input />
                </Form.Item>

                <Form.Item name="organisation_id" label="Компания" rules={[{ required: true, message: 'Пожалуйста, выберите компанию' }]}>
                  <Select
                    showSearch
                    optionFilterProp="children"
                    placeholder="Выберите компанию"
                    onChange={handleCompanyChange}
                  >
                    {organisations?.map(org => (
                      <Option key={org.id} value={org.organisation_id}>{org.name}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="department_id" label="Отдел" rules={[{ required: true, message: 'Пожалуйста, выберите отдел' }]}>
                  <Select
                    showSearch
                    optionFilterProp="children"
                    placeholder="Выберите отдел"
                  >
                    {filteredDepartments?.map(dept => (
                      <Option key={dept.id} value={dept.id}>{dept.name}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="city" label="Город" rules={[{ required: true, message: 'Пожалуйста, укажите город' }]}>
                  <Input />
                </Form.Item>

                <Form.Item name="job_address" label="Адрес рабочего места" rules={[{ required: true, message: 'Пожалуйста, укажите адрес рабочего места' }]}>
                  <Input />
                </Form.Item>

                <Form.Item name="is_hidden" valuePropName="checked" label="Скрытая вакансия">
                  <Button
                    type={isHiddenVacancy ? "primary" : "default"}
                    onClick={() => setIsHiddenVacancy(!isHiddenVacancy)}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: isHiddenVacancy ? 'bold' : 'normal'
                    }}
                  >
                    {isHiddenVacancy ? '✓ Скрытая вакансия' : 'Скрытая вакансия'}
                  </Button>
                </Form.Item>

              </Col>

              <Col span={12}>
                <Form.Item name="count" label="Количество позиций" rules={[{ required: true, message: 'Пожалуйста, укажите количество позиций' }]}>
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item name="rate" label="Ставка" rules={[{ required: true, message: 'Пожалуйста, выберите ставку' }]}>
                  <Select placeholder="Выберите ставку">
                    {['0.25', '0.5', '0.75', '1', '1.25', '1.5', '1.75', '2'].map(value => (
                      <Option key={value} value={value}>
                        {value}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="vacancy_reason" label="Причина возникновения вакансии" rules={[{ required: true, message: 'Пожалуйста, выберите причину' }]}>
                  <Select>
                    {vacancyReasons.map(reason => (
                      <Option key={reason} value={reason}>{reason}</Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item name="subordinate_count" label="Подчиненные (количество)" rules={[{ required: true, message: 'Пожалуйста, укажите количество подчиненных' }]}>
                  <InputNumber min={0} style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item name="closing_date" label="Планируемая дата закрытия вакансии">
                  <DatePicker
                    style={{ width: '100%' }}
                    showTime={false}
                    format="YYYY-MM-DD"
                  />
                </Form.Item>


                {isHiddenVacancy && (
                  <Form.Item
                    name="visible_user_ids"
                    label="Пользователи, которые смогут видеть вакансию"
                    rules={[
                      {
                        required: isHiddenVacancy,
                        message: 'Выберите хотя бы одного пользователя'
                      }
                    ]}
                  >
                    <Select
                      mode="multiple"
                      showSearch
                      optionFilterProp="children"
                      placeholder="Выберите пользователей"
                      style={{ width: '100%' }}
                    >
                      {users?.map(user => (
                        <Option key={user.id} value={user.id}>
                          {user.first_name} {user.last_name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
              </Col>
            </Row>
          </div>

          {/* Ответственные лица */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Ответственные лица
            </h3>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="author" label="Автор заявки (ФИО)" rules={[{ required: true, message: 'Пожалуйста, укажите автора заявки' }]}>
                  <Input readOnly />
                </Form.Item>

                <Form.Item name="creator_position" label="Автор заявки (должность)" rules={[{ required: true, message: 'Пожалуйста, укажите должность автора' }]}>
                  <Input />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="assigner_id" label="Рекрутер">
                  <Select showSearch optionFilterProp="children">
                    {selectedOrganisationId && organisationUsers?.length > 0
                      ? (() => {
                        // Фильтруем уникальных пользователей по user_id
                        const uniqueUsers = [];
                        const seenUserIds = new Set();
                        organisationUsers.forEach(user => {
                          const userId = user.user_id || user.id;
                          if (!seenUserIds.has(userId)) {
                            seenUserIds.add(userId);
                            uniqueUsers.push(user);
                          }
                        });
                        return uniqueUsers.map(user => (
                          <Option key={user.user_id || user.id} value={user.user_id || user.id}>
                            {user.first_name} {user.last_name}
                          </Option>
                        ));
                      })()
                      : users?.map(user => (
                        <Option key={user.id} value={user.id}>
                          {user.first_name} {user.last_name}
                        </Option>
                      ))
                    }
                  </Select>
                </Form.Item>

                <Form.Item name="supervisors" label="Начальник">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Даты и время */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Даты и время
            </h3>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="opening_date" label="Дата открытия вакансии" rules={[{ required: true, message: 'Пожалуйста, укажите дату подачи заявки' }]}>
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="planned_start_date" label="Планируемая дата выхода сотрудника на работу" rules={[{ required: true, message: 'Пожалуйста, укажите планируемую дату выхода' }]}>
                  {/* <DatePicker
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD"
                  /> */}
                  <Input placeholder="Можно указать дату, месяц или период (например: 01.09.2023, сентябрь 2023, 3-й квартал 2023)" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="work_start_time" label="Время начала работы">
                  <TimePicker
                    style={{ width: '100%' }}
                    format="HH:mm"
                    needConfirm={false}
                    placeholder="Выберите время"
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="work_end_time" label="Время окончания работы">
                  <TimePicker
                    style={{ width: '100%' }}
                    format="HH:mm"
                    needConfirm={false}
                    placeholder="Выберите время"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="work_schedule" label="График работы" rules={[{ required: true, message: 'Пожалуйста, укажите график работы' }]}>
                  <Select
                    options={workSchedules.map(schedule => ({ value: schedule, label: schedule }))}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="work_location_ratio" label="Соотношение работы: дом/офис">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Условия работы */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Условия работы
            </h3>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="business_trips_required" valuePropName="checked" label="Командировки">
                  <Button
                    type={businessTripsRequired ? "primary" : "default"}
                    onClick={() => setBusinessTripsRequired(!businessTripsRequired)}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: businessTripsRequired ? 'bold' : 'normal'
                    }}
                  >
                    {businessTripsRequired ? '✓ Командировки' : 'Командировки'}
                  </Button>
                </Form.Item>

                {businessTripsRequired && (
                  <Form.Item
                    name="business_trips_details"
                    label="Частота и длительность командировок"
                    rules={[{
                      validator: (_, value) => {
                        if (!businessTripsRequired) return Promise.resolve();
                        if (value && String(value).trim().length > 0) return Promise.resolve();
                        return Promise.reject(new Error('Пожалуйста, укажите частоту и длительность командировок'));
                      }
                    }]}
                  >
                    <Input />
                  </Form.Item>
                )}

                <Form.Item name="relocation_willingness" valuePropName="checked" label="Готовность к переезду">
                  <Button
                    type={relocationWillingness ? "primary" : "default"}
                    onClick={() => setRelocationWillingness(!relocationWillingness)}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: relocationWillingness ? 'bold' : 'normal'
                    }}
                  >
                    {relocationWillingness ? '✓ Готовность к переезду' : 'Готовность к переезду'}
                  </Button>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="communication_compensation" valuePropName="checked" label="Компенсация затрат: связь">
                  <Button
                    type={communicationCompensation ? "primary" : "default"}
                    onClick={() => setCommunicationCompensation(!communicationCompensation)}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: communicationCompensation ? 'bold' : 'normal'
                    }}
                  >
                    {communicationCompensation ? '✓ Компенсация затрат: связь' : 'Компенсация затрат: связь'}
                  </Button>
                </Form.Item>

                <Form.Item name="car_compensation" valuePropName="checked" style={{ display: 'none' }}>
                  <Input type="hidden" />
                </Form.Item>
                <Form.Item label="Компенсация затрат: личный автомобиль">
                  <Button
                    type={carCompensationValue ? "primary" : "default"}
                    onClick={() => handleCheckboxToggle('car_compensation')}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: carCompensationValue ? 'bold' : 'normal'
                    }}
                  >
                    {carCompensationValue ? '✓ Компенсация затрат: личный автомобиль' : 'Компенсация затрат: личный автомобиль'}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Описание работы */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Описание работы
            </h3>
            <Form.Item name="tasks" label="Задачи" rules={[{ required: true, message: 'Пожалуйста, опишите задачи' }]}>
              <TextArea rows={3} />
            </Form.Item>

            <Form.Item name="expected_results" label="Ожидаемые результаты" rules={[{ required: true, message: 'Пожалуйста, опишите ожидаемые результаты' }]}>
              <TextArea rows={3} />
            </Form.Item>

            <Form.Item name="responsibilities" label="Функциональные обязанности" rules={[{ required: true, message: 'Пожалуйста, опишите функциональные обязанности' }]}>
              <TextArea rows={3} />
            </Form.Item>

            <Form.Item name="reporting" label="Отчетность о проделанной работе">
              <TextArea rows={3} />
            </Form.Item>
          </div>

          {/* Зарплата */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Зарплата
            </h3>
            <Form.Item name="salary_structure" label="Структура заработной платы" rules={[{ required: true, message: 'Пожалуйста, укажите структуру заработной платы' }]}>
              <TextArea rows={2} />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Зарплата на испытательном сроке (BYN)"
                  validateStatus={form.getFieldError('net_probation_salary_min')?.length > 0 || form.getFieldError('net_probation_salary_max')?.length > 0 ? 'error' : ''}
                  help={form.getFieldError('net_probation_salary_min')?.[0] || form.getFieldError('net_probation_salary_max')?.[0]}
                >
                  <Input.Group compact>
                    <Form.Item name="net_probation_salary_min" noStyle >
                      <InputNumber
                        min={0}
                        placeholder="От"
                        style={{ width: '50%' }}
                        parser={value => value?.replace(/\s?BYN/g, '')}
                      />
                    </Form.Item>
                    <Form.Item
                      name="net_probation_salary_max"
                      noStyle
                      dependencies={["net_probation_salary_min"]}
                      rules={[{
                        validator: (_, value) => {
                          const min = form.getFieldValue('net_probation_salary_min');
                          const max = form.getFieldValue('net_probation_salary_max');
                          if (min !== undefined && min !== null && min !== '') return Promise.resolve();
                          if (max !== undefined && max !== null && max !== '') return Promise.resolve();
                          return Promise.reject(new Error('Укажите оклад на испытательном сроке (мин. или макс.)'));
                        }
                      }]}
                    >
                      <InputNumber
                        min={0}
                        placeholder="До"
                        style={{ width: '50%' }}
                        parser={value => value?.replace(/\s?BYN/g, '')}
                      />
                    </Form.Item>
                  </Input.Group>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Зарплата после испытательного срока (BYN)"
                  validateStatus={form.getFieldError('net_post_probation_salary_min')?.length > 0 || form.getFieldError('net_post_probation_salary_max')?.length > 0 ? 'error' : ''}
                  help={form.getFieldError('net_post_probation_salary_min')?.[0] || form.getFieldError('net_post_probation_salary_max')?.[0]}
                >
                  <Input.Group compact>
                    <Form.Item name="net_post_probation_salary_min" noStyle>
                      <InputNumber
                        min={0}
                        placeholder="От"
                        style={{ width: '50%' }}
                        parser={value => value?.replace(/\s?BYN/g, '')}
                      />
                    </Form.Item>
                    <Form.Item
                      name="net_post_probation_salary_max"
                      noStyle
                      dependencies={["net_post_probation_salary_min"]}
                      rules={[{
                        validator: (_, value) => {
                          const min = form.getFieldValue('net_post_probation_salary_min');
                          const max = form.getFieldValue('net_post_probation_salary_max');
                          if (min !== undefined && min !== null && min !== '') return Promise.resolve();
                          if (max !== undefined && max !== null && max !== '') return Promise.resolve();
                          return Promise.reject(new Error('Укажите оклад после испытательного срока (мин. или макс.)'));
                        }
                      }]}
                    >
                      <InputNumber
                        min={0}
                        placeholder="До"
                        style={{ width: '50%' }}
                        parser={value => value?.replace(/\s?BYN/g, '')}
                      />
                    </Form.Item>
                  </Input.Group>
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Требования к кандидату */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Требования к кандидату
            </h3>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="gender" label="Пол">
                  <Select>
                    <Option value="Мужской">Мужской</Option>
                    <Option value="Женский">Женский</Option>
                    <Option value="Не имеет значения">Не имеет значения</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="min_age" label="Минимальный возраст">
                  <InputNumber min={18} max={100} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="max_age" label="Максимальный возраст">
                  <InputNumber min={18} max={100} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="marital_status" label="Семейное положение">
                  <Select
                    mode="multiple"
                    options={[
                      { value: 'Холост/Не замужем', label: 'Холост/Не замужем' },
                      { value: 'Женат/Замужем', label: 'Женат/Замужем' },
                      { value: 'Разведен(а)', label: 'Разведен(а)' },
                      { value: 'Вдовец/Вдова', label: 'Вдовец/Вдова' },
                      { value: 'Не имеет значения', label: 'Не имеет значения' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="education" label="Образование">
                  <Select>
                    <Option value="secondary">Среднее</Option>
                    <Option value="specialized_secondary">Среднее специальное</Option>
                    <Option value="incomplete_higher">Неоконченное высшее</Option>
                    <Option value="higher">Высшее</Option>
                    <Option value="bachelor">Бакалавр</Option>
                    <Option value="master">Магистр</Option>
                    <Option value="phd">Кандидат наук</Option>
                    <Option value="doctor">Доктор наук</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                {/* Водительское удостоверение */}
                <Form.Item name="driver_license_required" valuePropName="checked" style={{ display: 'none' }}>
                  <Input type="hidden" />
                </Form.Item>
                <Form.Item label="Наличие водительского удостоверения">
                  <Button
                    type={driverLicenseRequiredValue ? "primary" : "default"}
                    onClick={() => handleCheckboxToggle('driver_license_required')}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: driverLicenseRequiredValue ? 'bold' : 'normal'
                    }}
                  >
                    {driverLicenseRequiredValue ? '✓ Наличие водительского удостоверения' : 'Наличие водительского удостоверения'}
                  </Button>
                </Form.Item>

                {driverLicenseRequiredValue && (
                  <Form.Item name="driver_license_categories" label="Категории водительских прав">
                    <Select
                      mode="multiple"
                      options={[
                        'A', 'A1', 'B', 'B1', 'C', 'C1', 'D', 'D1', 'BE', 'CE', 'C1E', 'DE', 'D1E'
                      ].map(cat => ({ value: cat, label: cat }))}
                    />
                  </Form.Item>
                )}
              </Col>
              <Col span={12}>
                <Form.Item name="has_car" valuePropName="checked" label="Наличие личного автомобиля">
                  <Button
                    type={hasCar ? "primary" : "default"}
                    onClick={() => setHasCar(!hasCar)}
                    style={{
                      width: '100%',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: hasCar ? 'bold' : 'normal'
                    }}
                  >
                    {hasCar ? '✓ Наличие личного автомобиля' : 'Наличие личного автомобиля'}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Навыки и опыт */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Навыки и опыт
            </h3>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="pc_skills" label="Уровень владения ПК (Word, Excel и другие)">
                  <Select
                    options={[
                      'Не требуется',
                      'Базовый',
                      'Уверенный',
                      'Продвинутый'
                    ].map(level => ({ value: level, label: level }))}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="skills_1c" label="Навыки в работе с 1С">
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Form.List name="software_knowledge">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={8} key={key}>
                      <Col span={12}>
                        <Form.Item {...restField} name={[name, 'program']} label="Программа">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={10}>
                        <Form.Item {...restField} name={[name, 'level']} label="Уровень владения">
                          <Select options={[
                            'Базовый',
                            'Уверенный',
                            'Продвинутый'
                          ].map(l => ({ value: l, label: l }))} />
                        </Form.Item>
                      </Col>
                      <Col span={2} style={{ marginTop: '6px', display: 'flex', alignItems: 'center' }}>
                        <Button danger onClick={() => remove(name)}>Удалить</Button>
                      </Col>
                    </Row>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block>
                      Добавить программу
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            <Form.List name="language_knowledge">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={8} key={key}>
                      <Col span={12}>
                        <Form.Item {...restField} name={[name, 'language']} label="Язык">
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={10}>
                        <Form.Item {...restField} name={[name, 'level']} label="Уровень владения">
                          <Select options={[
                            'Базовый',
                            'Уверенный',
                            'Продвинутый'
                          ].map(l => ({ value: l, label: l }))} />
                        </Form.Item>
                      </Col>
                      <Col span={2} style={{ marginTop: '6px', display: 'flex', alignItems: 'center' }}>
                        <Button danger onClick={() => remove(name)}>Удалить</Button>
                      </Col>
                    </Row>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block>
                      Добавить язык
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            <Form.Item name="work_experience" label="Опыт работы (область, продолжительность)">
              <TextArea rows={2} />
            </Form.Item>
          </div>

          {/* Личные и профессиональные качества */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Личные и профессиональные качества
            </h3>
            <Form.Item name="personal_qualities" label="Личные качества кандидатов">
              <TextArea rows={3} />
            </Form.Item>

            <Form.Item name="professional_skills" label="Профессиональные навыки кандидатов">
              <TextArea rows={3} />
            </Form.Item>
          </div>

          {/* Дополнительные требования */}
          <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
              Дополнительные требования
            </h3>
            <Form.Item name="preferred_companies" label="Компании, из которых предпочтительно рассматривать кандидатов">
              <TextArea rows={2} />
            </Form.Item>

            <Form.Item name="unwanted_companies" label="Компании, из которых нежелательно рассматривать кандидатов">
              <TextArea rows={2} />
            </Form.Item>

            <Form.Item name="other_conditions" label="Другие условия">
              <TextArea rows={3} />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      <ConfirmModal
        visible={showConfirm}
        onConfirm={handleConfirmClose}
        onCancel={() => setShowConfirm(false)}
        title="Подтверждение закрытия"
        message="Данные не будут сохранены. Продолжить?"
        okText="Продолжить"
        cancelText="Отмена"
        type="warning"
      />
    </>
  );
};

export default EditModal;